import { useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

const Dialog = styled(StyledDialog)`
  width: 400px;
  text-align: left;
`;

const Heading = styled(Text)`
  margin: 0 0 16px;
  text-align: center;
`;

const Footer = styled.div`
  padding: 24px 32px 32px;
  display: flex;
  box-shadow: 0px -1px 8px 0px #2222220f;
  gap: 24px;
  margin: 32px -24px -40px;
`;

const FooterButton = styled(Button)`
  flex: 1;
`;

type FormDataT = {
  csv: File | null;
};

interface Props {
  onClose: VoidFunction;
}

const ImportModal: FC<Props> = ({ onClose }) => {
  const [errors, setErrors] = useState<Array<{ row: number; errors: string }>>(
    [],
  );
  const alerts = useAlerts();

  const toasts = useToasts();

  const queryClient = useQueryClient();

  const {
    control,
    formState: { isSubmitting },
    setError,
    clearErrors,
    handleSubmit,
  } = useForm<FormDataT>({
    defaultValues: {
      csv: null,
    },
  });

  const onSubmit = () => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const formData = new FormData();

        if (data.csv) {
          formData.append('csv', data.csv);
        }

        await api.post('/v1/bp-article-reels/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        await queryClient.invalidateQueries(['bp_article_reels']);

        onClose();

        toasts.create('success', 'Articles imported');
      } catch (err) {
        handleFormErrors(err, setError, alerts);
        if (axios.isAxiosError(err)) {
          const error = err as AxiosError<{
            errors: Array<{ row: number; errors: string }>;
          }>;

          setErrors(error.response?.data?.errors ?? []);
        }
      }
    })();
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Heading $type="heading2">Import file</Heading>
        <FormLayout>
          <FormField<FormDataT>
            id="csv-file-input"
            type="file"
            accept="text/csv"
            control={control}
            notes="Format: CSV"
            name="csv"
          />
          {!!errors.length && (
            <Larger>
              {errors.map((err, i) => (
                <ValidationMessage key={i}>
                  Row {err.row}: {err.errors}
                </ValidationMessage>
              ))}
            </Larger>
          )}
        </FormLayout>
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Footer>
          <FooterButton
            type="button"
            color="primary"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={onSubmit}
          >
            Upload
          </FooterButton>
          <FooterButton type="button" color="secondary" onClick={onClose}>
            Cancel
          </FooterButton>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default ImportModal;
